/**
 * Browse hubs for governance actions.
 *
 * These exist to be crawled: each is a stable URL gathering one slice of the
 * corpus, giving search engines a themed entry point and every proposal an
 * internal link from at least two places. "cardano drep list" is won by an
 * indexable directory page — this is the governance-action equivalent.
 *
 * They live under /governance_actions/browse/<slug> because the sibling
 * segment is already the dynamic [id] route; two dynamic segments cannot
 * coexist at one level, and a `browse` prefix keeps both unambiguous.
 */

import {
  deriveProposalState,
  type IndexedProposal,
  type ProposalState,
} from './proposal';

export interface ProposalHub {
  slug: string;
  /**
   * Whether this hub gathers a lifecycle state or an action type. A proposal
   * belongs to exactly one type hub and at most one state hub, so the two are
   * looked up separately when building breadcrumbs.
   */
  kind: 'state' | 'type';
  /** Page heading and title-tag subject. */
  title: string;
  /**
   * Short label for the chip row.
   *
   * Separate from `title` because the two have different jobs: the title is
   * written for a search result ("Expired Cardano Governance Actions"), which
   * as a button reads like a sentence and made the row unusable.
   */
  label: string;
  /** Meta description; kept under ~155 characters. */
  description: string;
  /** Short intro rendered above the list. */
  intro: string;
  matches: (proposal: IndexedProposal) => boolean;
}

function byState(state: ProposalState) {
  return (proposal: IndexedProposal) => deriveProposalState(proposal) === state;
}

function byType(...types: string[]) {
  return (proposal: IndexedProposal) =>
    types.includes(proposal.proposal?.type || '');
}

export const PROPOSAL_HUBS: ProposalHub[] = [
  {
    slug: 'active',
    label: 'Open for voting',
    kind: 'state',
    title: 'Active Cardano Governance Actions',
    description:
      'Cardano governance actions currently open for DRep voting, with type, requested amount and the epoch each one expires.',
    intro:
      'These governance actions are open for voting right now. DReps can still cast a vote, and ADA holders can still change who represents them before the voting period closes.',
    matches: byState('active'),
  },
  {
    slug: 'enacted',
    label: 'Enacted',
    kind: 'state',
    title: 'Enacted Cardano Governance Actions',
    description:
      'Every Cardano governance action that reached ratification and was enacted on-chain, with the epoch it took effect.',
    intro:
      'These governance actions passed the required voting thresholds and were enacted on-chain. They are the permanent record of what Cardano governance has actually decided.',
    matches: byState('enacted'),
  },
  {
    slug: 'expired',
    label: 'Expired',
    kind: 'state',
    title: 'Expired Cardano Governance Actions',
    description:
      'Cardano governance actions that reached the end of their voting period without ratification, with submission and expiry epochs.',
    intro:
      'These governance actions ran out their voting period without reaching the thresholds needed to pass. They remain on-chain as a record of what was proposed and rejected.',
    matches: byState('expired'),
  },
  {
    slug: 'treasury-withdrawals',
    label: 'Treasury withdrawal',
    kind: 'type',
    title: 'Cardano Treasury Withdrawal Proposals',
    description:
      'Every treasury withdrawal proposed through Cardano governance, with the amount of ADA requested and whether it was enacted or expired.',
    intro:
      'Treasury withdrawals ask the Cardano treasury to fund a project or organisation. They are the most common governance action by a wide margin, and the ones with the most ADA at stake.',
    matches: byType('TreasuryWithdrawals'),
  },
  {
    slug: 'info-actions',
    label: 'Info action',
    kind: 'type',
    title: 'Cardano Info Action Proposals',
    description:
      'Cardano info actions — non-binding governance actions used to record community sentiment on a question.',
    intro:
      'Info actions carry no on-chain effect. They exist to put a question to DReps, SPOs and the Constitutional Committee and record the answer on-chain.',
    matches: byType('InfoAction'),
  },
  {
    slug: 'parameter-changes',
    label: 'Parameter change',
    kind: 'type',
    title: 'Cardano Protocol Parameter Change Proposals',
    description:
      'Governance actions that change Cardano protocol parameters — fees, deposits, block sizes and governance thresholds.',
    intro:
      'Parameter change actions adjust how the Cardano protocol itself behaves, from transaction fees and deposits to the thresholds governance uses to pass future actions.',
    matches: byType('ParameterChange'),
  },
  {
    slug: 'constitutional-committee',
    label: 'Committee update',
    kind: 'type',
    title: 'Cardano Constitutional Committee Proposals',
    description:
      'Governance actions that add, remove or replace members of the Cardano Constitutional Committee.',
    intro:
      'These actions change the makeup of the Constitutional Committee, the body that judges whether a governance action is constitutional before it can be enacted.',
    matches: byType('NewCommittee', 'UpdateCommittee'),
  },
  {
    slug: 'constitution',
    label: 'New constitution',
    kind: 'type',
    title: 'Cardano Constitution Proposals',
    description:
      'Governance actions proposing a new or amended Cardano Constitution, with ratification status and epoch.',
    intro:
      'The Constitution sets the rules every other governance action is judged against. Changing it requires the highest thresholds in Cardano governance.',
    matches: byType('NewConstitution'),
  },
  {
    slug: 'hard-forks',
    label: 'Hard fork',
    kind: 'type',
    title: 'Cardano Hard Fork Initiation Proposals',
    description:
      'Hard fork initiation actions that move Cardano to a new protocol version, with the epoch each took effect.',
    intro:
      'Hard fork initiation actions upgrade the Cardano protocol to a new major version. They require agreement from both DReps and stake pool operators.',
    matches: byType('HardForkInitiation'),
  },
];

export function findHub(slug: string): ProposalHub | undefined {
  return PROPOSAL_HUBS.find((hub) => hub.slug === slug);
}

/**
 * The type hub a proposal belongs to, if any.
 *
 * Matched with the hubs' own predicates rather than by slugifying the type
 * name. Those two spellings agreed only for treasury withdrawals — the
 * derived slugs were `info-action`, `parameter-change`, `hard-fork-initiation`
 * and `new-committee` against hub slugs `info-actions`, `parameter-changes`,
 * `hard-forks` and `constitutional-committee` — so every other proposal
 * silently lost its breadcrumb and its "all of this type" link.
 */
export function hubForType(proposal: IndexedProposal): ProposalHub | undefined {
  return PROPOSAL_HUBS.find(
    (hub) => hub.kind === 'type' && hub.matches(proposal)
  );
}

/** The lifecycle hub a proposal currently falls in, if one exists. */
export function hubForState(
  proposal: IndexedProposal
): ProposalHub | undefined {
  return PROPOSAL_HUBS.find(
    (hub) => hub.kind === 'state' && hub.matches(proposal)
  );
}
